/**
 * 导航。桌面常驻,手机是抽屉(§6b)。
 * 每一项标出这一页现在是真数据还是没有数据源 —— 让人一眼知道哪几页能当真。
 */
import { Link, NavLink, useLocation } from 'react-router-dom';

interface NavItem {
  path: string;
  label: string;
  icon: string;
  /** live = 整页真数据;partial = 一部分有源一部分没有(页内逐项标注);
   *  demo = 原型 mock;none = 没有数据源 */
  state: 'live' | 'partial' | 'demo' | 'none';
}

const navItems: NavItem[] = [
  { path: '/console', label: '总览', icon: 'ri-dashboard-line', state: 'live' },
  { path: '/devices', label: '设备', icon: 'ri-robot-line', state: 'live' },
  // 视频墙(2026-08-08 起):画面、帧率/码率/RTT/丢包都是真的(经 hub 中继的 WHEP)。
  // 仍缺的只有画面上的检测框 —— 狗在检测, 但没有东西把框推到云端。
  { path: '/live', label: '视频墙', icon: 'ri-live-line', state: 'partial' },
  { path: '/tasks', label: '任务', icon: 'ri-task-line', state: 'live' },
  // 地图(2026-08-08 起):设备位姿(遥测新增)、命名地点、记忆物体全是真的;
  // 缺的是经纬度底图(锚点未标定)与禁区(P4 配置下发)。所以是 partial 不是 demo ——
  // 标成 demo 会让人以为图上那个机器人也是编的。
  // 地图与指挥舱合并成一个指挥面:看全队在哪 + 按地点派任务(走台账)。
  { path: '/maps', label: '指挥舱', icon: 'ri-radar-line', state: 'partial' },
  // ★ 「指挥舱」这一页删了(2026-08-09)。它不在产品规格的信息架构里 ——
  //   spec-fleet-console.md 定义的是 10 页, 全文没有"指挥舱/cockpit"这个词,
  //   它是原型多画出来的第 11 页, 从来没有过功能定义。
  //   接上真数据之后它和「地图」几乎逐像素相同, 而两页做同一件事时,
  //   先烂掉的那一页和好的那一页长得一模一样。名单去「设备」, 位置去「地图」。
  { path: '/knowledge', label: '认知', icon: 'ri-brain-line', state: 'partial' },
  // 运维(2026-08-09 起):版本矩阵与组件健康已接真源(设备遥测);OTA/远程日志仍没有,
  // 配置下发是【故意不做】(仓库是配置的唯一事实源, 见 §0.1)。
  { path: '/ops', label: '运维', icon: 'ri-tools-line', state: 'partial' },
  { path: '/network', label: '网络', icon: 'ri-wifi-line', state: 'live' },
  // 报表(2026-08-09 起):数字来自设备现场日志(missions.db)的汇总, 云端不重算。
  // 缺的只有"覆盖率增长曲线"(要按时间存档的覆盖数据, 现在只有当前值)。
  { path: '/reports', label: '报表', icon: 'ri-bar-chart-line', state: 'partial' },
  { path: '/settings', label: '设置', icon: 'ri-settings-3-line', state: 'live' },
];

const stateMark: Record<NavItem['state'], { cls: string; title: string } | null> = {
  live: null,
  partial: { cls: 'bg-sky-400', title: '部分接真:页内标注哪些有源、哪些没有' },
  demo: { cls: 'bg-amber-400', title: '演示数据:此页尚未接后端' },
  none: { cls: 'bg-background-300', title: '暂无数据源' },
};

export default function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const location = useLocation();

  return (
    <>
      {open && <div className="fixed inset-0 bg-foreground-950/30 z-40 lg:hidden" onClick={onClose} />}
      <aside className={`fixed left-0 top-0 bottom-0 w-56 bg-background-50 border-r border-background-200/70 flex flex-col z-50 transition-transform duration-200 lg:translate-x-0 ${open ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-14 flex items-center px-5 border-b border-background-200/70 shrink-0">
          {/* 品牌回首页 `/`(landing)。默认惯例是回后台首页 /console,但那条路侧栏第一项
              "总览"已经有了,再来一次是重复;而从控制台【出去】看首页此前没有任何入口。
              2026-07-25 用户定。
              onClose 不能省 —— 手机上侧边栏是抽屉,只跳路由不关抽屉的话
              页面在背后换了、抽屉还盖着,看起来像"点了没反应"。 */}
          <Link
            to="/"
            onClick={onClose}
            aria-label="返回首页"
            className="flex items-center rounded -mx-1 px-1 py-0.5 cursor-pointer hover:opacity-70 transition-opacity"
          >
            <span className="font-heading text-lg font-bold text-foreground-900 tracking-tight">0xman</span>
            <span className="ml-2 text-xs font-mono text-accent-600 bg-accent-100/80 px-1.5 py-0.5 rounded">HUB</span>
          </Link>
          <button onClick={onClose} className="ml-auto w-9 h-9 flex items-center justify-center rounded hover:bg-background-100 cursor-pointer lg:hidden">
            <i className="ri-close-line text-foreground-500" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {navItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            const mark = stateMark[item.state];
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mb-0.5 whitespace-nowrap cursor-pointer ${
                  isActive ? 'bg-primary-100/70 text-primary-700' : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                }`}
              >
                <span className={`w-5 h-5 flex items-center justify-center ${isActive ? 'text-primary-600' : 'text-foreground-400'}`}>
                  <i className={`${item.icon} text-base`}></i>
                </span>
                {item.label}
                {mark && <span className={`ml-auto w-1.5 h-1.5 rounded-full ${mark.cls}`} title={mark.title} />}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-4 py-3 border-t border-background-200/70 shrink-0 space-y-1.5">
          <div className="flex items-center gap-2 text-[11px] text-foreground-500">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />演示数据
          </div>
          <div className="flex items-center gap-2 text-[11px] text-foreground-500">
            <span className="w-1.5 h-1.5 rounded-full bg-background-300" />暂无数据源
          </div>
        </div>
      </aside>
    </>
  );
}
